/** @format */

import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { storeRef } from '../index';
import { refresh } from '../store/slices';
import { hasTenant } from './tenant.helpers';

interface AppError extends AxiosError {
  data: any;
}

const _handleError = async (
  error: AppError,
  tries?: number,
  altMessage?: string
) => {
  const requestUrl = error?.config?.url || '';
  const isTenantLookup =
    requestUrl.includes('/tenant/domain/') ||
    requestUrl.includes('/tenant/slug/');

  // Tenant resolution tries domain first then slug; surface errors only
  // when both fail via the tenant slice, not as intermediate toasts.
  if (isTenantLookup) return;

  if (error?.response?.status === 400) {
    const temp: any = error?.response?.data || error;

    if (temp.message) {
      if (temp.message.includes('code must match /^[A-Z0-9]{3,10}/')) {
        return toast.error(
          'Code must be 3-10 characters long and contain only uppercase letters and numbers'
        );
      }
      return toast.error(temp.message);
    }

    if (typeof error === 'string') {
      return toast.error(`${error} please check your internet`);
    }

    delete temp['status_code'];

    if (typeof temp !== 'object') {
      toast.error(`Something went wrong, please try again`);
    } else {
      const toastId = '400-toast';
      Object.keys(temp).forEach((key) => {
        temp[key][0] &&
          toast.error(
            typeof temp[key] === 'string'
              ? temp[key]
              : temp[key][0] || altMessage || 'An error occurred, try again.',
            { toastId, position: toast.POSITION.TOP_RIGHT }
          );
      });
    }
    return;
  }

  if (error?.response?.status === 403 || error?.response?.status === 404) {
    let errorMessage;

    if (!(error?.response?.data as any)?.message) {
      errorMessage = 'Something went wrong, please try again later';
    } else {
      typeof (error?.response?.data as any)?.message === 'string'
        ? (errorMessage = (error?.response?.data as any)?.message)
        : (errorMessage = (error?.response?.data as any)?.message[0]);
    }

    return toast.error(errorMessage);
  }

  if (error?.response?.status === 401 && typeof tries === 'number') {
    if (error.response.data) toast.error((error.response.data as any).message);

    if (altMessage !== 'refresh') {
      if (!hasTenant()) {
        // Tenant not loaded yet — skip refresh, it would fail without tenant ID
        return;
      }
      if (tries <= 3) {
        toast.error('Unauthorized, your token will be refreshed');
        return storeRef.dispatch(refresh());
      } else {
        toast.error(
          'Unauthorized, please try again with different credentials'
        );
      }
    }
  }

  if (error?.response?.status === 500) {
    toast.error(
      'There was an error completing your request. Please try again later'
    );
    return;
  }

  const customId = '429-toast';
  if (error?.response?.status === 429) {
    toast.error('Too many requests, please try again later', {
      position: toast.POSITION.TOP_RIGHT,
      toastId: customId,
    });
    return;
  }
};

const saveSession = ({
  accessToken,
  expiresIn,
  refreshToken,
}: {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
}) => {
  localStorage.setItem('accessToken', accessToken as string);
  // localStorage.setItem("login_time", JSON.stringify(new Date().getTime()));
  localStorage.setItem('refreshToken', refreshToken as string);
  // localStorage.setItem("expiresIn", expiresIn.toString());
};

export { _handleError, saveSession };
